import { EXPERIENCE_LOGOS, PROJECT_LOGOS } from "@/assets/static/images";
import Company, { WorkMode } from "@/types/Company";

export const EXPERIENCES: Company[] = [
    {
        id: "heldio",
        name: "Heldio",
        description: "An early-stage startup building an AI-powered, adaptive language-learning platform around learners' real-world goals.",
        logo: EXPERIENCE_LOGOS.heldio,
        roles: [
            {
                title: "Back-End Developer",
                started_date: "2 Feb 2026",
            },
        ],
        started_date: "2 Feb 2026",
        achievements: [
            "Build the backend foundation with NestJS, Fastify, and TypeScript, keeping API contracts and domain responsibilities clearly defined.",
            "Develop backend authentication flows, session management, and ownership checks, backed by PostgreSQL and Drizzle migrations.",
            "Implement the initial course-creation and learner Home APIs with validated contracts and safeguards against duplicate requests.",
            "Maintain automated backend quality checks, API documentation, and container-based staging delivery as the platform evolves.",
        ],
        work_mode: WorkMode.REMOTE,
    },
    {
        id: "zire20",
        name: "Zire20",
        description: "A large-scale eCommerce platform aiming for a smooth and efficient shopping experience.",
        logo: PROJECT_LOGOS.zire20,
        roles: [
            {
                title: "Full-Stack Developer",
                started_date: "Dec 25’",
            },
            {
                title: "Front-End Developer",
                started_date: "Oct 25’",
                ended_date: "Dec 25’",
            },
        ],
        started_date: "Oct 25’",
        achievements: [
            "Optimized core customer purchase journeys on a high-traffic storefront, resolving UI state inconsistencies to improve overall system reliability and conversion rates.",
            "Enhanced front-end performance by refactoring complex, bloated components and eliminating redundant React re-render cycles on critical paths.",
            "Introduced a highly responsive, reusable component library to establish layout uniformity and significantly reduce front-end design regression debt.",
            "Collaborated closely with backend engineers to align API integration contracts, standardizing type-safe error boundaries and resolving production deployment blockers.",
        ],
        work_mode: WorkMode.HYBRID,
        endorsement: {
            quote: "Emran's expertise in front-end development was instrumental in delivering a seamless shopping experience for our customers.",
            author: "Sarah Johnson (Product Manager)",
            title: "Zire20",
        },
    },
    {
        id: "atlas-mall",
        name: "Atlas Mall",
        description: "A Persian eCommerce company with a responsive storefront and an administration workspace for retail operations.",
        logo: PROJECT_LOGOS.atlasMall,
        roles: [
            {
                title: "Solo Full-Stack Developer & Designer",
                started_date: "22 Dec 2025",
                ended_date: "5 Sep 2026",
            },
        ],
        started_date: "22 Dec 2025",
        ended_date: "5 Sep 2026",
        achievements: [
            "Designed and developed the Persian storefront and administration workspace as the company's solo developer.",
            "Built product discovery, comparison, customer accounts, and checkout with transactional inventory and payment verification.",
            "Implemented a shared payment interface across 13 provider adapters, an append-only wallet ledger, and tag-versioned Redis caching.",
            "Created a responsive RTL interface with localized pricing, light and dark themes, and catalog-connected shopping assistance.",
        ],
        work_mode: WorkMode.HYBRID,
    },
    {
        id: "sourena",
        name: "Sourena Perfumery",
        description: "A growing perfume brand focused on high-quality fragrances and wellness products.",
        logo: PROJECT_LOGOS.sourena,
        roles: [
            {
                title: "Full-Stack Developer & Designer",
                started_date: "Nov 24’",
                ended_date: "Nov 25’",
            },
        ],
        started_date: "Nov 24’",
        ended_date: "Nov 25’",
        achievements: [
            "Architected and deployed a modular full-stack eCommerce platform utilizing Next.js and Spring Boot (Kotlin), designed for long-term scalability and structured catalog expansion.",
            "Engineered an automated multi-currency and multi-language translation engine that integrates live exchange rate APIs to deliver seamless localized customer experiences.",
            "Developed an administrative Content Management System (CMS) featuring granular role-based access control (RBAC), enabling secure self-serve catalog updates for non-technical staff.",
            "Established a unified design system and shared component library leveraging structured SCSS mixins, accelerating visual consistency and front-end development velocity.",
        ],
        work_mode: WorkMode.ON_SITE,
        endorsement: {
            quote: "Emran delivered a robust eCommerce system that streamlined our global operations. The custom CMS and multi-currency updates eliminated our operational overhead completely.",
            author: "Dr Amin Salim (Founder)",
            title: "Sourena Perfumery",
        },
    },
    {
        id: "inkart",
        name: "Inkart",
        description:
            "A startup building customizable NFC cards and digital profiles for personal and professional use.",
        logo: PROJECT_LOGOS.inkart,
        roles: [
            {
                title: "Full-Stack Developer & Designer",
                started_date: "Mar 25’",
                ended_date: "Sep 25’",
            },
        ],
        started_date: "Mar 25’",
        ended_date: "Sep 25’",
        achievements: [
            "Engineered a low-latency interactive profile editor for customizable NFC cards, streamlining user customization workflows and reducing interaction friction.",
            "Built a template-driven dynamic rendering engine, enabling rapid integration of new visual layouts with zero regression risk to legacy templates.",
            "Implemented strict client-side validation rules and robust API contracts to ensure data integrity and prevent unexpected UI states.",
            "Refactored onboarding and card-design flows based on interaction telemetry data, resulting in a substantial reduction in user drop-off rates.",
        ],
        work_mode: WorkMode.REMOTE,
    },
    {
        id: "zarin-electronic",
        name: "Zarin Electronic",
        description: "A company providing renewable energy solutions and IoT-enabled smart devices.",
        logo: PROJECT_LOGOS.zarinElectronic,
        roles: [
            {
                title: "Contract Android Developer",
                started_date: "May 25’",
                ended_date: "Oct 25’",
            },
        ],
        started_date: "May 25’",
        ended_date: "Oct 25’",
        achievements: [
            "Engineered high-performance real-time telemetry dashboards for smart IoT devices, optimizing visual data layouts for on-site technicians.",
            "Designed a highly resilient Bluetooth Low Energy (BLE) connection recovery module featuring exponential backoff, ensuring data continuity in metallic environments.",
            "Refactored mobile state management using Jetpack Compose and Kotlin Flows, ensuring consistent 60fps UI rendering on legacy field hardware.",
            "Hardened exception handling and state synchronization mechanisms, eliminating application crashes during network dropouts and high-frequency telemetry bursts.",
        ],
        work_mode: WorkMode.REMOTE,
    },
];
